import type { SxProps } from '@mui/material';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { RadioGroup } from '@ovComponents/2-component/radio/radio';
import { Stack, Typography, useTheme } from 'hephaestus/web';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompletionBox } from '../completionBox/completionBox';
import {
  type SelectionTileOption,
  SelectionTileRadioOption,
} from './selectionTileRadioOption';

type SelectionTileProps = {
  direction?: 'row' | 'column';
  type?: 'checkbox' | 'radio' | 'tile' | 'completion';
  value?: string | number;
  options: SelectionTileOption[];
  onChange: (event: any) => void;
  error?: boolean;
  errorText?: string;
  component?: 'SUGGESTED' | 'OTHER' | 'DEFAULT';
  selectedComponent?: 'SUGGESTED' | 'OTHER' | 'DEFAULT';
  infoTooltip?: string;
  testId?: string;
  label?: string;
  disabled?: boolean;
  xs?: number;
  sx?: SxProps;
  innerSx?: SxProps;
};

export const SelectionTile = ({
  direction = 'column',
  type = 'radio',
  value,
  infoTooltip,
  options,
  error,
  errorText,
  component,
  selectedComponent,
  testId,
  label,
  disabled = false,
  xs,
  sx,
  innerSx,
  ...restProps
}: SelectionTileProps) => {
  const theme = useTheme();
  const { t } = useTranslation('components');
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const helpTextIdPrefix = useId();

  const [changing, setChanging] = useState(false);
  const onChange = async (event: any) => {
    setChanging(true);
    try {
      await restProps.onChange(event);
    } finally {
      setChanging(false);
    }
  };

  if (type === 'radio') {
    return (
      <>
        {error && (
          <HelpText
            text={errorText ?? t('fieldErrors.selectionTile')}
            tone='error'
          />
        )}
        <RadioGroup
          testId={testId}
          value={value}
          infoTooltip={infoTooltip}
          label={label}
          onChange={onChange}
          fullWidth
          disabled={disabled || changing}
          sx={sx}
        >
          <Stack
            direction={direction}
            spacing={2}
            useFlexGap
            sx={{
              width: '100%',
              flexGrow: 1,
              flexWrap: direction === 'row' ? 'wrap' : undefined,
            }}
          >
            {(options || []).map((option, index) => (
              <SelectionTileRadioOption
                key={index}
                option={option}
                index={index}
                testId={testId}
                direction={direction}
                xs={xs}
                groupDisabled={disabled}
                changing={changing}
                helpTextId={`${helpTextIdPrefix}-help-${index}`}
                onSelect={(optionValue) => {
                  // The click does not await. Catch so a rejected handler
                  // stays an unlock in `finally`, not an unhandled rejection.
                  void onChange({ target: { value: optionValue } }).catch(
                    () => undefined,
                  );
                }}
              />
            ))}
          </Stack>
        </RadioGroup>
      </>
    );
  }

  if (type === 'completion') {
    return (
      <>
        {(options || []).map((option, index) => (
          <CompletionBox
            testId={testId}
            disabled={disabled || changing}
            title={option.label}
            titleSideComponent={option.labelSideComponent}
            state='next'
            variant='edit'
            type={type}
            onAdd={async () => {
              setSelectedIndex(index);
              await onChange({ target: { value: option.value } });
            }}
            key={option.value}
            selected={
              selectedComponent === component && selectedIndex === index
            }
            sx={sx}
            innerSx={innerSx}
          >
            <Typography
              variant='bodyLarge'
              data-testid={
                testId
                  ? `${testId}-subtitle-${option.label}`
                  : `completion-box-subtitle${option.subtitle}`
              }
              sx={{ color: theme.color.onSurfaceVariant }}
            >
              {option.subtitle}
            </Typography>
          </CompletionBox>
        ))}
      </>
    );
  }

  return (
    // TODO: Checkbox
    <></>
  );
};

export default SelectionTile;
